"use client";

import Link from "next/link";
import { trackEvent, trackMetaEvent } from "@/lib/analytics";

interface TrackedCtaProps {
  href: string;
  label: string;
  location: string; // e.g. "article_anxietate", "hero", "colaborare_terapeuti"
  ctaType?: "find_therapist" | "enter_app" | "therapist_signup";
  target?: "_blank";
  rel?: string;
  className?: string;
  children: React.ReactNode;
}

function appendAttribution(base: string): string {
  if (!base.includes("app.mindtale.ro")) return base;
  if (typeof window === "undefined") return base;
  try {
    const stored = window.localStorage.getItem("mindtale_attribution_last_v1");
    if (!stored) return base;
    const attr = JSON.parse(stored) as Record<string, string | null>;
    const url = new URL(base);
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
     "fbclid", "gclid", "gbraid", "wbraid"].forEach((key) => {
      if (attr[key]) url.searchParams.set(key, attr[key]!);
    });
    return url.toString();
  } catch {
    return base;
  }
}

export default function TrackedCta({
  href,
  label,
  location,
  ctaType = "find_therapist",
  target,
  rel,
  className,
  children,
}: TrackedCtaProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent("cta_click", { cta_type: ctaType, location, label });
    if (ctaType === "therapist_signup") {
      trackMetaEvent("InitiateSignup", { signup_type: "therapist", location });
    } else if (ctaType === "enter_app") {
      trackMetaEvent("InitiateSignup", { signup_type: "client", location });
    } else {
      trackMetaEvent("FindTherapist", { location });
    }

    // Build URL with attribution at click time (localStorage is populated by then)
    if (href.includes("app.mindtale.ro")) {
      e.preventDefault();
      const dest = appendAttribution(href);
      if (target === "_blank") {
        window.open(dest, "_blank", "noopener,noreferrer");
      } else {
        window.location.href = dest;
      }
    }
  };

  return (
    <Link href={href} target={target} rel={rel} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
}
